import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, PackageCheck, Package, Clock4 } from "lucide-react";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { useTableSort, SortableHeader } from "@/lib/tableSort";
import { toDMY } from "@/lib/dateOnly";

const STATUS_PILL = {
  to_be_dispatch: { label: "To be Dispatch", bg: "#FFF4E0", color: "#8A5A00", border: "#F1DFB5" },
  dispatched: { label: "Dispatched", bg: "#E1F5EE", color: "#085041", border: "#C0E4D5" },
};

const fmtNum = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtDMY = (iso) => toDMY(iso);

// Local YYYY-MM-DD for "today" — used as the default Final Pickup Date range.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const KpiCard = ({ icon: Icon, label, value, accent, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: accent.bg, color: accent.color }}>
          <Icon className="h-4 w-4" />
        </span>
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
      </div>
      <p className="text-2xl font-semibold text-[#0F0F0F]" style={{ color: accent.color }}>{value}</p>
    </CardContent>
  </Card>
);

export default function DispatchAnalytics() {
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState({ summary: {}, skus: [], invoices: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/dispatch/analytics`, { params, withCredentials: true });
      setData(res.data || { summary: {}, skus: [], invoices: [] });
    } catch {
      toast.error("Failed to load dispatch analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateFrom, dateTo]);

  const s = data.summary || {};

  const { sortedRows: sortedSkus, sort: skuSort, toggleSort: toggleSkuSort } = useTableSort(data.skus || [], {
    accessors: {
      sku_id: (r) => r.sku_id || "",
      product_name: (r) => r.product_name || "",
      brand: (r) => r.brand || "",
      planned: (r) => r.planned || 0,
      dispatched: (r) => r.dispatched || 0,
      pending: (r) => r.pending || 0,
    },
  });

  const { sortedRows: sortedInvoices, sort: invSort, toggleSort: toggleInvSort } = useTableSort(data.invoices || [], {
    accessors: {
      invoice_number: (r) => r.invoice_number || "",
      seller_name: (r) => r.seller_name || "",
      final_pickup_date: (r) => (r.final_pickup_date ? new Date(r.final_pickup_date).getTime() : 0),
      quantity_planned: (r) => r.quantity_planned || 0,
      quantity_sent: (r) => r.quantity_sent || 0,
      quantity_pending: (r) => r.quantity_pending || 0,
      dispatch_status: (r) => r.dispatch_status || "",
      transporter_name: (r) => r.transporter_name || "",
    },
  });

  const cards = useMemo(() => ([
    { key: "inv", icon: FileText, label: "Total Invoices", value: fmtNum(s.total_invoices), accent: { bg: "#E6F1FB", color: "#0C447C" }, testId: "dispatch-kpi-total-invoices" },
    { key: "planned", icon: Package, label: "Total Units Planned", value: fmtNum(s.total_units_planned), accent: { bg: "#F1EFE8", color: "#444441" }, testId: "dispatch-kpi-units-planned" },
    { key: "sent", icon: PackageCheck, label: "Total Units Dispatched", value: fmtNum(s.total_units_dispatched), accent: { bg: "#E1F5EE", color: "#085041" }, testId: "dispatch-kpi-units-dispatched" },
    { key: "pending", icon: Clock4, label: "Total Units Pending", value: fmtNum(s.total_units_pending), accent: { bg: "#FEE2E2", color: "#991B1B" }, testId: "dispatch-kpi-units-pending" },
  ]), [s]);

  return (
    <div className="space-y-5" data-testid="dispatch-analytics">
      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">Filter by Final Pickup Date:</span>
        <DateRangeFilter from={dateFrom} to={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} testId="dispatch-analytics-date-range" />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <KpiCard key={c.key} icon={c.icon} label={c.label} value={c.value} accent={c.accent} testId={c.testId} />
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
        </div>
      ) : (
        <>
          {/* SKU Summary */}
          <Card>
            <CardContent className="pt-4">
              <h2 className="text-sm font-semibold text-[#0F0F0F] mb-3">SKU Summary ({sortedSkus.length})</h2>
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-auto max-h-[420px]">
                  <Table containerClassName="overflow-visible">
                    <TableHeader className="sticky top-0 z-20">
                      <TableRow className="bg-slate-100 hover:bg-slate-100">
                        <SortableHeader sortKey="sku_id" sort={skuSort} onToggle={toggleSkuSort} className="bg-slate-100 border-b-2 border-slate-200">SKU Code</SortableHeader>
                        <SortableHeader sortKey="product_name" sort={skuSort} onToggle={toggleSkuSort} className="bg-slate-100 border-b-2 border-slate-200">Product Name</SortableHeader>
                        <SortableHeader sortKey="brand" sort={skuSort} onToggle={toggleSkuSort} className="bg-slate-100 border-b-2 border-slate-200">Brand</SortableHeader>
                        <SortableHeader sortKey="planned" sort={skuSort} onToggle={toggleSkuSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Planned Qty</SortableHeader>
                        <SortableHeader sortKey="dispatched" sort={skuSort} onToggle={toggleSkuSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Dispatched Qty</SortableHeader>
                        <SortableHeader sortKey="pending" sort={skuSort} onToggle={toggleSkuSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Pending Qty</SortableHeader>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedSkus.length === 0 ? (
                        <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No SKUs for the selected Final Pickup Date range.</TableCell></TableRow>
                      ) : (
                        sortedSkus.map((r) => (
                          <TableRow key={r.sku_id} data-testid={`dispatch-analytics-sku-${r.sku_id}`}>
                            <TableCell className="font-mono text-xs font-semibold">{r.sku_id}</TableCell>
                            <TableCell className="text-xs">{r.product_name || "—"}</TableCell>
                            <TableCell className="text-xs">{r.brand || "—"}</TableCell>
                            <TableCell className="text-xs text-right font-mono">{fmtNum(r.planned)}</TableCell>
                            <TableCell className="text-xs text-right font-mono">{fmtNum(r.dispatched)}</TableCell>
                            <TableCell className={`text-xs text-right font-mono ${r.pending > 0 ? "text-red-700 font-semibold" : ""}`}>{fmtNum(r.pending)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Summary */}
          <Card>
            <CardContent className="pt-4">
              <h2 className="text-sm font-semibold text-[#0F0F0F] mb-3">Invoice Summary ({sortedInvoices.length})</h2>
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-auto max-h-[480px]">
                  <Table containerClassName="overflow-visible">
                    <TableHeader className="sticky top-0 z-20">
                      <TableRow className="bg-slate-100 hover:bg-slate-100">
                        <SortableHeader sortKey="invoice_number" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Invoice Number</SortableHeader>
                        <SortableHeader sortKey="seller_name" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Seller Name</SortableHeader>
                        <SortableHeader sortKey="final_pickup_date" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Final Pickup Date</SortableHeader>
                        <SortableHeader sortKey="quantity_planned" sort={invSort} onToggle={toggleInvSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Qty Planned</SortableHeader>
                        <SortableHeader sortKey="quantity_sent" sort={invSort} onToggle={toggleInvSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Qty Sent</SortableHeader>
                        <SortableHeader sortKey="quantity_pending" sort={invSort} onToggle={toggleInvSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Qty Pending</SortableHeader>
                        <SortableHeader sortKey="dispatch_status" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Status</SortableHeader>
                        <SortableHeader sortKey="transporter_name" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Transporter</SortableHeader>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedInvoices.length === 0 ? (
                        <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No invoices for the selected Final Pickup Date range.</TableCell></TableRow>
                      ) : (
                        sortedInvoices.map((r) => {
                          const st = STATUS_PILL[r.dispatch_status] || STATUS_PILL.to_be_dispatch;
                          return (
                            <TableRow key={r.id} data-testid={`dispatch-analytics-invoice-${r.id}`}>
                              <TableCell className="font-mono text-xs font-semibold" data-testid={`dispatch-analytics-invoice-number-${r.id}`}>
                                {r.invoice_number || <span className="text-muted-foreground font-sans font-normal">Not Uploaded</span>}
                              </TableCell>
                              <TableCell className="text-xs">{r.seller_name || "—"}</TableCell>
                              <TableCell className="text-xs">{fmtDMY(r.final_pickup_date)}</TableCell>
                              <TableCell className="text-xs text-right font-mono">{fmtNum(r.quantity_planned)}</TableCell>
                              <TableCell className="text-xs text-right font-mono">{fmtNum(r.quantity_sent)}</TableCell>
                              <TableCell className={`text-xs text-right font-mono ${r.quantity_pending > 0 ? "text-red-700 font-semibold" : ""}`}>{fmtNum(r.quantity_pending)}</TableCell>
                              <TableCell>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border" style={{ background: st.bg, color: st.color, borderColor: st.border }}>
                                  {st.label}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs">{r.transporter_name || "—"}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
